import { useState } from "react";
import { Plus, Edit, Trash2, Eye, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAdminData, useAdminCrud } from "@/hooks/useAdminData";

const emptyForm = {
  title: "", slug: "", excerpt: "", content: "", image_url: "",
  category: "trasporti", tags: "", meta_title: "", meta_description: "",
  status: "bozza",
};

export default function AdminBlog() {
  const { data: articles, reload } = useAdminData("blog");
  const { create, update, remove } = useAdminCrud("blog");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm }); setShowForm(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ title: a.title, slug: a.slug, excerpt: a.excerpt, content: a.content, image_url: a.image_url || "", category: a.category, tags: (a.tags || []).join(", "), meta_title: a.meta_title || "", meta_description: a.meta_description || "", status: a.status });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Inserisci il titolo"); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (editId) {
        await update(editId, payload);
        toast.success("Articolo aggiornato");
      } else {
        await create(payload);
        toast.success("Articolo creato");
      }
      setShowForm(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try {
      await remove(id);
      toast.success("Articolo eliminato");
      reload();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-500">{articles.length} articoli totali</p>
        <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors" data-testid="create-blog-btn">
          <Plus size={16} /> Nuovo Articolo
        </button>
      </div>

      {/* Articles Table */}
      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Titolo</th>
                <th className="text-left px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Categoria</th>
                <th className="text-left px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Stato</th>
                <th className="text-left px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Data</th>
                <th className="text-right px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {articles.map((a) => (
                <tr key={a.id} className="hover:bg-zinc-50" data-testid={`blog-row-${a.slug}`}>
                  <td className="px-4 py-3 font-body font-medium text-[#09090B]">{a.title}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest text-zinc-500 font-heading">{a.category}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 font-bold uppercase tracking-widest font-heading ${a.status === "pubblicato" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-600"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-body">{new Date(a.updated_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {a.status === "pubblicato" && (
                        <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-zinc-400 hover:text-[#FF5200]" title="Visualizza"><Eye size={14} /></a>
                      )}
                      <button onClick={() => openEdit(a)} className="p-1.5 text-zinc-400 hover:text-[#FF5200]" title="Modifica" data-testid={`edit-blog-${a.slug}`}><Edit size={14} /></button>
                      <button onClick={() => handleDelete(a.id)} className="p-1.5 text-zinc-400 hover:text-red-500" title="Elimina" data-testid={`delete-blog-${a.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-auto" data-testid="blog-form-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto mb-6">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-heading text-lg font-bold uppercase">{editId ? "Modifica Articolo" : "Nuovo Articolo"}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Titolo *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="Titolo dell'articolo" data-testid="blog-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Slug</label>
                  <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="auto-generato" />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Categoria</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white">
                    <option value="trasporti">Trasporti</option>
                    <option value="logistica">Logistica</option>
                    <option value="guide">Guide</option>
                    <option value="news">News</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Anteprima</label>
                <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none h-20 resize-none" placeholder="Breve descrizione dell'articolo..." data-testid="blog-excerpt-input" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Contenuto (HTML)</label>
                <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none h-48 resize-y font-mono" placeholder="<h2>Titolo sezione</h2><p>Testo...</p>" data-testid="blog-content-input" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">URL Immagine</label>
                <input type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Tags (separati da virgola)</label>
                <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="trasporti, logistica, pallet" />
              </div>

              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading">SEO</legend>
                <div className="space-y-3 mt-2">
                  <input type="text" value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                    className="w-full h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="Meta Title (auto dal titolo)" />
                  <input type="text" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                    className="w-full h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none" placeholder="Meta Description (auto dall'anteprima)" />
                </div>
              </fieldset>

              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Stato</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none bg-white" data-testid="blog-status-select">
                  <option value="bozza">Bozza</option>
                  <option value="pubblicato">Pubblicato</option>
                </select>
              </div>

              <button type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50"
                data-testid="blog-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {editId ? "Aggiorna Articolo" : "Pubblica Articolo"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
